/**
 * This is a client for the menus backend service.
 *
 * These apis are public (no-auth) and are shared by both, the dashboard and the app.
 */
import {
  GetAllResolvedEdiblesResponse,
  GetMenuAppMetadataRequest,
  GetMenuAppMetadataResponse,
  GetMenuAppRestaurantDataRequest,
  GetMenuAppRestaurantDataResponse,
  GetMenuRequest,
  GetMenuResponseV2,
  ResolveEdibleRequest,
  ResolvedEdible,
} from "api-types";
import { AxiosInstance } from "axios";

export const getMenuV2 = async (
  client: AxiosInstance,
  restaurantUuid: string,
  restrictedLocOrRestId: string,
  menuId?: string,
  isViewableFilter?: boolean
): Promise<GetMenuResponseV2 | undefined> => {
  const getMenuRequest: GetMenuRequest = {
    restaurantUuid,
    restrictedLocOrRestId,
    menuId,
    isViewableFilter,
  };
  const { data, status } = await client.post(
    "/menus/get-menu-v2",
    getMenuRequest,
    {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    }
  );

  if (status === 404) {
    // the body is `{error: "No menu found for restaurant <uuid>"}` so we can't use the data
    return undefined;
  }

  return data;
};

/**
 * Returns all taxonomy data and an optional list of ingredient details.
 *
 * @param ingredientIds an optional list of ingredient ids for which we want to fetch ingredient details.
 * @returns
 */
export const getMenuAppMetadata = async (
  client: AxiosInstance,
  igfdTaxIngredientIds?: string[]
): Promise<GetMenuAppMetadataResponse> => {
  const getMenuAppMetadataRequest: GetMenuAppMetadataRequest = {
    igfdTaxIngredientIds,
  };
  const { data } = await client.post<GetMenuAppMetadataResponse>(
    "/menus/get-menu-app-metadata",
    getMenuAppMetadataRequest
  );
  return data;
};

/** Returns Restaurant info by city and url slug */
export const getMenuAppRestaurantData = async (
  client: AxiosInstance,
  city: string,
  urlSlug: string
): Promise<GetMenuAppRestaurantDataResponse | undefined> => {
  const getMenuAppRestaurantDataRequest: GetMenuAppRestaurantDataRequest = {
    city,
    urlSlug,
  };
  const { data, status } = await client.post<GetMenuAppRestaurantDataResponse>(
    "/menus/get-menu-app-restaurant-data",
    getMenuAppRestaurantDataRequest,
    {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    }
  );

  if (status === 404) {
    return undefined;
  }

  return data;
};

/** Returns resolved edibled (dishes and products) of a restaurant */
export const getAllResolvedEdibles = async (
  client: AxiosInstance,
  restaurantUuid: string
): Promise<GetAllResolvedEdiblesResponse | undefined> => {
  const { data } = await client.post("/menus/get-all-resolved-edibles", {
    restaurantUuid,
  });
  return data;
};

/**
 * Given a set of constituent ingredients and products, returns the
 * resolved allergens and preferences
 */
export const resolveEdible = async (
  client: AxiosInstance,
  request: ResolveEdibleRequest
): Promise<ResolvedEdible | undefined> => {
  const { data } = await client.post("/menus/resolve-edible", request);
  return data;
};
